// Operator credential for uploads in the isolated demo (Addendum B): asked explicitly at runtime, kept only in this
// page's memory, sent only as the Authorization header of POST /uploads on this host, forgotten on rejection.
import './operator.css';

let current = null; // { token, publish }

export const hasOperator = () => !!current;
export const operatorToken = () => (current ? current.token : null);
export const operatorPublishes = () => !!(current && current.publish);
export function forgetOperator() { current = null; }

/**
 * Ask for the operator credential (or reuse the one entered on this page).
 * @param {{ needPublish: boolean, accent?: string, lang?: 'en'|'es' }} o needPublish: files without a password must be
 *   published as public demo files, otherwise their link could not be opened by anyone.
 * @returns {Promise<{token:string, publish:boolean}|null>} null when the operator cancels
 */
export function requestOperator({ needPublish, accent, lang = 'en' } = {}) {
  if (current && (!needPublish || current.publish)) return Promise.resolve(current);
  const T = lang === 'es' ? ES : EN;
  return new Promise((resolve) => {
    const d = document.createElement('dialog');
    d.className = 'op-dialog';
    if (accent) d.style.setProperty('--op-accent', accent);
    d.setAttribute('aria-labelledby', 'op-title');
    d.innerHTML = `<form method="dialog" class="op-form" novalidate>
      <h2 id="op-title">${T.title}</h2>
      <p>${T.body}</p>
      <label for="op-secret">${T.label}</label>
      <input id="op-secret" type="password" autocomplete="off" spellcheck="false" required>
      ${needPublish ? `<label class="op-check"><input type="checkbox" id="op-publish"> <span>${T.publish}</span></label>` : ''}
      <p class="op-err" role="alert" hidden></p>
      <div class="op-actions"><button type="button" value="cancel" class="op-btn">${T.cancel}</button><button type="submit" value="ok" class="op-btn op-btn--primary">${T.submit}</button></div>
    </form>`;
    document.body.appendChild(d);
    const input = d.querySelector('#op-secret'), err = d.querySelector('.op-err'), pub = d.querySelector('#op-publish');
    const finish = (v) => { d.close(); d.remove(); resolve(v); };
    d.querySelector('[value=cancel]').addEventListener('click', () => finish(null));
    d.addEventListener('cancel', (e) => { e.preventDefault(); finish(null); });
    d.querySelector('form').addEventListener('submit', (e) => {
      e.preventDefault();
      const token = input.value.trim();
      if (!token) { err.textContent = T.empty; err.hidden = false; input.setAttribute('aria-invalid', 'true'); input.focus(); return; }
      if (needPublish && !pub.checked) { err.textContent = T.mustPublish; err.hidden = false; pub.focus(); return; }
      input.value = '';
      current = { token, publish: !!(pub && pub.checked) };
      finish(current);
    });
    d.showModal();
    input.focus();
  });
}

const EN = {
  title: 'Operator sign-in for demo uploads',
  body: 'This isolated demo accepts uploads only from its operator. Enter the operator credential for this environment. It stays in this tab’s memory, is sent only to this site’s transfer API, and is forgotten when the tab closes or the server refuses it.',
  label: 'Operator credential',
  publish: 'Publish these files as public demo files. Anyone with the link can download them, and visitors who turn sharing on may pass verified pieces to each other.',
  cancel: 'Cancel', submit: 'Continue', empty: 'Enter the operator credential.',
  mustPublish: 'Files without a password can only be opened from their link if they are public. Tick the box or cancel.',
};
const ES = {
  title: 'Acceso de operador para subir archivos de demostración',
  body: 'Esta demo aislada sólo acepta subidas de su operador. Escribe la credencial de operador de este entorno. Se guarda sólo en la memoria de esta pestaña, se envía sólo a la API de transferencias de este sitio y se olvida al cerrar la pestaña o si el servidor la rechaza.',
  label: 'Credencial de operador',
  publish: 'Publicar estos archivos como archivos públicos de demostración. Cualquiera con el enlace podrá descargarlos.',
  cancel: 'Cancelar', submit: 'Continuar', empty: 'Escribe la credencial de operador.',
  mustPublish: 'Sin contraseña, el enlace sólo funciona si el archivo es público. Marca la casilla o cancela.',
};
